import { useSelector } from 'app-redux'
import { RoomAddress, UserRole } from 'common/types'
import { useEffect, useRef } from 'react'
import useParamTogglerApi from 'features/param-toggler/param-toggler-api'
import { selectParam } from 'features/param-toggler/param-toggler-redux'
import { useJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useWorkflowUser } from 'room/common/use-workflow-user'
import {
	getAutoResumePendingStorageKey,
	writeAutoResumePending,
} from './recording-auto-resume-storage'
import { isRecordingPauseRoom, isRecordingRoom } from './recording-room-types'

/**
 * Pauses the trainer's recording when they are moved from the training room
 * to a lobby or group room: marks an auto-resume as pending and turns the
 * training room's `recording` param off.
 *
 * The decision uses the param value last seen IN the training room. Params
 * are room scoped (reset on SET_JOINED_ROOM), and the training room's stream
 * closes before the toggle-off echo could arrive.
 *
 * Coming back is handled by useRecordingTrainer: it shows the resume prompt
 * (getDisplayMedia needs the trainer's click) and turns the param on again
 * only when the trainer confirms.
 */
const useAutomaticRecordingTransition = (): void => {
	const address = useJoinedRoomAddress()
	const workflowUser = useWorkflowUser()
	const paramTogglerApi = useParamTogglerApi()
	const recordingParam = useSelector(state => selectParam(state, 'recording'))
	const previousAddressRef = useRef<RoomAddress | null>(null)
	const recordingEnabledInRecordingRoomRef = useRef(false)

	useEffect(() => {
		const workflowUserId = workflowUser?.id

		if (
			!address ||
			workflowUser?.role !== UserRole.UPPER_ECHELON ||
			!workflowUserId
		) {
			previousAddressRef.current = null
			recordingEnabledInRecordingRoomRef.current = false
			return
		}

		const previousAddress = previousAddressRef.current
		previousAddressRef.current = address

		if (!isRecordingPauseRoom(address.roomType)) {
			if (isRecordingRoom(address.roomType)) {
				recordingEnabledInRecordingRoomRef.current =
					recordingParam === true || recordingParam === 'true'
			}
			return
		}

		const leftRecordingRoom =
			!!previousAddress &&
			previousAddress.roomId !== address.roomId &&
			isRecordingRoom(previousAddress.roomType)

		if (!leftRecordingRoom || !recordingEnabledInRecordingRoomRef.current) {
			return
		}

		recordingEnabledInRecordingRoomRef.current = false

		const autoResumePendingStorageKey = getAutoResumePendingStorageKey(
			address.workflowId,
			address.interactionSchemeId,
			workflowUserId
		)

		console.log(
			'[AutoRecordingTransition] Left the recording room while recording — pausing',
			{
				from: previousAddress.roomType,
				to: address.roomType,
				storageKey: autoResumePendingStorageKey,
			}
		)
		// Kept even if the toggle-off fails: the recording is paused anyway (the
		// pause-room rule in useRecordingTrainer stops it), so the trainer must
		// still be offered the resume when they come back.
		writeAutoResumePending(autoResumePendingStorageKey, true)
		void paramTogglerApi
			.toggleOffParam(previousAddress, 'recording')
			.catch(error => {
				console.warn(
					'Failed to automatically pause recording on room transition',
					error
				)
			})
	}, [
		address,
		paramTogglerApi,
		recordingParam,
		workflowUser?.id,
		workflowUser?.role,
	])
}

export default useAutomaticRecordingTransition
